import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchUtxos, fetchTxids } from '@/lib/explorer';

export type PaymentStatus = 'idle' | 'waiting' | 'detected' | 'error';

interface DetectedPayment {
  txid: string;
  amount: number; // in BCH
}

/** Live diagnostics for the detection loop, surfaced for an on-screen debug panel. */
export interface DetectionDebug {
  polls: number;          // how many times we've polled
  lastUtxoCount: number;  // UTXOs seen on the last successful poll
  lastTxCount: number;    // txs seen on the last successful poll
  lastPolledAt: number;   // timestamp of last completed poll
  lastError: string;      // last error message (empty if none)
  backend: string;        // which explorer backend answered
  address: string;        // address being watched (bare)
  expectedBCH: number;    // amount we're waiting for
}

// Poll every 5 seconds — fast enough for a POS experience.
const POLL_INTERVAL_MS = 5_000;

/**
 * Fetch UTXOs (unspent outputs) for an address, in BCH.
 * The proxy returns satoshis + which backend answered + any backend errors.
 */
async function fetchAddressUtxos(
  address: string
): Promise<{ utxos: { txid: string; value: number }[]; backend: string | null; errors: string[] }> {
  const res = await fetchUtxos(address);
  return {
    utxos: res.utxos.map((u) => ({ txid: u.txid, value: u.value / 1e8 })), // sats → BCH
    backend: res.backend,
    errors: res.errors,
  };
}

/** Fetch recent transaction ids for an address. */
async function fetchAddressTxids(
  address: string
): Promise<{ txids: string[]; backend: string | null; errors: string[] }> {
  const res = await fetchTxids(address);
  return { txids: res.txids, backend: res.backend, errors: res.errors };
}

export function usePaymentDetection(
  address: string,
  expectedBCH: number,
  active: boolean,
  onPaymentDetected: (payment: DetectedPayment) => void
) {
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [debug, setDebug] = useState<DetectionDebug>({
    polls: 0, lastUtxoCount: 0, lastTxCount: 0, lastPolledAt: 0,
    lastError: '', backend: '', address: '', expectedBCH: 0,
  });
  const knownTxids = useRef<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);
  const onDetected = useRef(onPaymentDetected);
  onDetected.current = onPaymentDetected;

  const poll = useCallback(async () => {
    if (!address || expectedBCH <= 0) return;
    // Skip this tick if the previous request is still in-flight
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    let utxoCount = 0;
    let txCount = 0;
    let backend = '';
    let backendErrors: string[] = [];
    try {
      // Strategy 1: Check UTXOs for amount-matched payments.
      const { utxos, backend: utxoBackend, errors } = await fetchAddressUtxos(address);
      utxoCount = utxos.length;
      backend = utxoBackend || '';
      backendErrors = errors;
      console.debug(
        `[payment-detection] UTXOs=${utxos.length} via ${backend || 'none'} (expect ≥ ${(expectedBCH * 0.8).toFixed(8)} BCH)`,
        utxos.map((u) => ({ txid: u.txid.slice(0, 10), bch: u.value })),
        errors.length ? { backendErrors: errors } : ''
      );
      setError(null);
      for (const utxo of utxos) {
        if (!knownTxids.current.has(utxo.txid)) {
          // Accept any new UTXO that is at least 80% of the expected amount.
          if (utxo.value >= expectedBCH * 0.8) {
            console.debug(`[payment-detection] MATCH via UTXO ${utxo.txid}`);
            knownTxids.current.add(utxo.txid);
            setStatus('detected');
            onDetected.current({ txid: utxo.txid, amount: utxo.value });
            return;
          }
        }
      }

      // Strategy 2: Check transaction history for ANY new txid (catches 0-conf
      // payments the wallet already spent/consolidated).
      try {
        const { txids, backend: txBackend } = await fetchAddressTxids(address);
        txCount = txids.length;
        if (txBackend) backend = txBackend;
        console.debug(`[payment-detection] txs=${txids.length} via ${txBackend || 'none'}`);
        for (const txid of txids) {
          if (!knownTxids.current.has(txid)) {
            console.debug(`[payment-detection] MATCH via new tx ${txid}`);
            knownTxids.current.add(txid);
            setStatus('detected');
            onDetected.current({ txid, amount: expectedBCH });
            return;
          }
        }
      } catch (e) {
        console.debug('[payment-detection] tx-history check failed (non-fatal):', e);
      }
    } catch (e) {
      console.warn('[payment-detection] explorer error:', e);
      setError('Could not connect to BCH explorer');
      setDebug((d) => ({
        ...d,
        polls: d.polls + 1,
        lastPolledAt: Date.now(),
        lastError: e instanceof Error ? e.message : String(e),
        address: address.replace(/^bitcoincash:/i, ''),
        expectedBCH,
      }));
      inFlightRef.current = false;
      return;
    }
    // Successful poll (no match yet) — record diagnostics. If every backend
    // failed server-side, surface that in the debug panel.
    setDebug((d) => ({
      ...d,
      polls: d.polls + 1,
      lastUtxoCount: utxoCount,
      lastTxCount: txCount,
      lastPolledAt: Date.now(),
      lastError: backend ? '' : (backendErrors.join('; ') || 'all backends failed'),
      backend: backend || 'none',
      address: address.replace(/^bitcoincash:/i, ''),
      expectedBCH,
    }));
    inFlightRef.current = false;
  }, [address, expectedBCH]);

  useEffect(() => {
    if (!active || !address || expectedBCH <= 0) {
      setStatus('idle');
      return;
    }

    setStatus('waiting');
    setError(null);
    knownTxids.current = new Set();
    inFlightRef.current = false;

    let cancelled = false;

    // ── Snapshot first ────────────────────────────────────────────────────────
    // Fetch existing UTXOs AND transaction history, adding them all to
    // knownTxids BEFORE starting the polling loop. This prevents any
    // pre-existing UTXO/transaction from triggering a false-positive.
    Promise.all([
      fetchAddressUtxos(address).catch(() => ({ utxos: [], backend: null, errors: [] })),
      fetchAddressTxids(address).catch(() => ({ txids: [], backend: null, errors: [] })),
    ]).then(([utxoRes, txRes]) => {
      if (cancelled) return;
      for (const utxo of utxoRes.utxos) {
        knownTxids.current.add(utxo.txid);
      }
      for (const txid of txRes.txids) {
        knownTxids.current.add(txid);
      }
      // Do an immediate first poll right after snapshot (no 5s wait)
      poll();
      // Then continue polling at the regular interval
      intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    });

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, address, expectedBCH, poll]);

  const reset = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setStatus('idle');
    setError(null);
    knownTxids.current = new Set();
    inFlightRef.current = false;
  }, []);

  return { status, error, reset, debug };
}
